import queryString from 'query-string';
import { useState } from 'react';

interface IListingComponentState<TData> {
  readonly data: TData;
  readonly loading: boolean;
  readonly error: string;
  readonly isInitialData: boolean;
  readonly pushToUrl?: boolean;
}

// Extend the hook definition with TData for the data structure
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useListingComponentData = <TFormData extends Record<string, any>, TData>(
  apiEndpoint: string,
  initialData: TData,
  skipEmptyString = true,
  pushToUrl = true
) => {
  const [state, setState] = useState<IListingComponentState<TData>>({
    data: initialData,
    loading: false,
    error: '',
    isInitialData: true,
  });

  const fetchData = async (formData: TFormData) => {
    try {
      setState((prev) => ({
        ...prev,
        loading: true,
        error: '',
      }));

      const requestQueryString = `?${queryString.stringify(formData, {
        skipEmptyString: skipEmptyString,
        arrayFormat: 'comma',
      })}`;

      const response = await fetch(`${apiEndpoint}${requestQueryString}`);

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const data = await response.json();

      if (pushToUrl) {
        const currentUrl = new URL(window.location.href);
        const searchParams = currentUrl.searchParams;

        (Object.keys(formData) as Array<keyof TFormData>).forEach((key) => {
          const value = formData[key];
          searchParams.set(key?.toString(), value?.toString());
        });

        window.history.pushState({}, '', currentUrl.href);
      }

      setState((prev) => ({
        ...prev,
        data,
        loading: false,
        isInitialData: false,
      }));
    } catch (error) {
      setState((state) => ({
        ...state,
        loading: false,
        error: 'Error fetching data',
      }));
    }
  };

  return { fetchData, state };
};

export default useListingComponentData;
